import { useEffect, useRef, useState } from "react";
import { ScanBarcode, X } from "lucide-react";
import { api } from "@/lib/api";

const COUNT_KEY = "snaplist.scanSession";
const HIDE_MS = 7000;
const money = (n) => (n == null ? "" : `$${Number(n).toFixed(2)}`);

// Every handheld scan: flash the eBay value (median asking price for that barcode) and count the session.
export default function ScanFlash() {
  const [count, setCount] = useState(() => Number(sessionStorage.getItem(COUNT_KEY)) || 0);
  const [scan, setScan] = useState(null);
  const timer = useRef(null);
  useEffect(() => {
    const onKS = ({ detail }) => setScan((s) => (s?.code === detail.code ? { ...s, decided: detail.action } : s));
    window.addEventListener("snaplist:keep-skip", onKS);
    return () => window.removeEventListener("snaplist:keep-skip", onKS);
  }, []);
  useEffect(() => {
    const onScan = async ({ detail: code }) => {
      setCount((c) => { sessionStorage.setItem(COUNT_KEY, String(c + 1)); return c + 1; });
      setScan({ code, status: "looking" });
      clearTimeout(timer.current);
      try {
        const { data } = await api.get(`/media-batches/lookup/${code}`);
        setScan((s) => (s?.code === code ? { ...data, code, decided: s.decided } : s));
      } catch {
        setScan((s) => (s?.code === code ? { code, status: "error" } : s));
      }
      timer.current = setTimeout(() => setScan(null), HIDE_MS);
    };
    window.addEventListener("snaplist:usb-scan", onScan);
    return () => { window.removeEventListener("snaplist:usb-scan", onScan); clearTimeout(timer.current); };
  }, []);
  const reset = () => { sessionStorage.removeItem(COUNT_KEY); setCount(0); };
  if (!count) return null;
  return (
    <div className="pointer-events-none fixed bottom-4 right-4 z-[60] flex w-[min(20rem,calc(100vw-2rem))] flex-col items-end gap-2" data-testid="scan-flash">
      {scan && (
        <div key={scan.code} className="pointer-events-auto w-full animate-in fade-in slide-in-from-bottom-2 rounded-2xl border border-slate-200 bg-white p-3 shadow-xl" data-testid="scan-flash-card">
          <div className="flex items-start gap-3">
            {scan.image ? <img src={scan.image} alt="" className="h-14 w-14 shrink-0 rounded-lg object-cover" /> : <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-lg bg-slate-100"><ScanBarcode className="h-5 w-5 text-slate-400" /></div>}
            <div className="min-w-0 flex-1">
              <p className="font-mono text-[10px] text-slate-400">{scan.code}</p>
              {scan.status === "looking" && <p className="mt-1 animate-pulse text-sm font-semibold text-slate-500" data-testid="scan-flash-looking">Checking eBay…</p>}
              {scan.status === "done" && <>
                <p className="font-mono text-2xl font-bold text-emerald-600" data-testid="scan-flash-price">{money(scan.price)}</p>
                <p className="truncate text-[11px] text-slate-600" data-testid="scan-flash-title">{scan.title}</p>
                <p className="text-[10px] text-slate-400">{scan.comps} on eBay · {money(scan.low)}–{money(scan.high)}</p>
              </>}
              {scan.status === "none" && <p className="mt-1 text-sm font-semibold text-amber-700" data-testid="scan-flash-none">No eBay listings for this barcode</p>}
              {scan.status === "error" && <p className="mt-1 text-sm font-semibold text-rose-600" data-testid="scan-flash-error">Couldn't check the price</p>}
              {scan.decided ? (
                <p className={`mt-1 text-[11px] font-bold uppercase ${scan.decided === "keep" ? "text-emerald-700" : "text-slate-500"}`} data-testid="scan-flash-decided">{scan.decided === "keep" ? "Thumbs up · going to Keepers" : "Skipped"}</p>
              ) : window.snaplistKeepSkip && (
                <p className="mt-1 text-[10px] text-slate-500" data-testid="scan-flash-keys">Press <kbd className="rounded border border-slate-300 px-1 font-mono">K</kbd> keep · <kbd className="rounded border border-slate-300 px-1 font-mono">S</kbd> skip</p>
              )}
            </div>
            <button type="button" onClick={() => setScan(null)} aria-label="Close" className="shrink-0 rounded p-0.5 text-slate-400 hover:text-slate-700" data-testid="scan-flash-close"><X className="h-3.5 w-3.5" /></button>
          </div>
        </div>
      )}
      <div className="pointer-events-auto inline-flex items-center gap-2 rounded-full bg-slate-900 py-1.5 pl-3 pr-1.5 text-xs font-semibold text-white shadow-lg" data-testid="scan-count-badge">
        <ScanBarcode className="h-3.5 w-3.5 text-emerald-400" />
        <span><b className="font-mono" data-testid="scan-count">{count}</b> scanned this session</span>
        <button type="button" onClick={reset} className="rounded-full bg-white/10 px-2 py-0.5 text-[10px] hover:bg-white/20" data-testid="scan-count-reset">Reset</button>
      </div>
    </div>
  );
}
